import { State } from 'redux/initialState';
import { FC, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslate } from 'hooks/useTranslate';

import { setModal } from 'modules/_shared/redux/modalActions';
import CustomLoading from 'modules/_shared/components/CustomLoading';
import successSwal from 'utils/successSwal';
import errorSwal from 'utils/errorSwal';
import { hasPartnersDecoupled } from 'utils/showDetails';
import { useGetCouncilEnvelopes } from 'modules/administratives/pages/CouncilDetails/hooks/useGetCouncilEnvelopes';
import useGetEnvelopeFromType from 'modules/_shared/components/EnvelopeSignatureStatus/hooks/useGetEnvelopeFromType';
import { Document } from 'types';
import RequestSignatureModalBody from './components/RequestSignatureModalBody';
import RequestSignatureModalFooter from './components/RequestSignatureModalFooter';
import RequestSignatureModalHeader from './components/RequestSignatureModalHeader';
import { requestSignature } from './utils/requestSignature';
import { useGetMissingSigners } from './hooks/useGetMissingSigners';
import { updateInactiveEnvelope } from './utils/updateInactiveEnvelope';

type Props = {
  councilId: string;
  templateType: string;
  document: Document;
  signatureType: string;
};

const RequestSignatureModal: FC<Props> = ({
  councilId,
  templateType,
  document,
  signatureType,
}: Props) => {
  const { t } = useTranslate();
  const dispatch = useDispatch();

  const councils = useSelector((state: State) => state.councils);
  const council = councils.find((council) => council._id === councilId);

  const actualSociety = useSelector(
    (state: State) => state.society.actualSociety
  );
  const user = useSelector((state: State) => state.user);
  const partners = useSelector((state: State) => state.partners);

  const { hasMissingSigners, missingText } = useGetMissingSigners({
    council,
  }).data;

  const { councilEnvelopes } = useGetCouncilEnvelopes({ councilId }).data;
  const { envelope } = useGetEnvelopeFromType(councilEnvelopes, signatureType);

  const [includeAssistants, setIncludeAssistants] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const handleCloseModal = () => {
    dispatch(setModal(null));
  };

  const handleChangeIncludeAssistants = () => {
    setIncludeAssistants((prev) => !prev);
  };

  const handleRequestSignature = async () => {
    try {
      const currentPartners = hasPartnersDecoupled()
        ? partners
        : actualSociety?.partners;

      setIsLoading(true);
      if (!council) return;
      await updateInactiveEnvelope(envelope?._id);

      await requestSignature({
        council,
        actualSociety,
        partners: currentPartners,
        signatureType,
        document,
        userId: user?._id,
        includeAssistants,
      });

      successSwal(t('SignatureRequestedSuccessfully'));
    } catch {
      errorSwal(t('ErrorRequestingSignature'));
    } finally {
      setIsLoading(false);
      handleCloseModal();
    }
  };

  return (
    <>
      <RequestSignatureModalHeader
        templateType={templateType}
        handleCloseModal={handleCloseModal}
      />

      <RequestSignatureModalBody
        hasMissingSigners={hasMissingSigners}
        missingText={missingText}
        includeAssistants={includeAssistants}
        templateType={templateType}
        handleChangeIncludeAssistants={handleChangeIncludeAssistants}
      />

      <RequestSignatureModalFooter
        hasMissingSigners={hasMissingSigners}
        handleRequestSignature={handleRequestSignature}
      />

      {isLoading && <CustomLoading />}
    </>
  );
};

export default RequestSignatureModal;
